// rebuild: regenera preview ATW-289 (imagem Docker do hosting-api removida pelo cleaner)
import {
  CountdownColorSlots,
  CountdownColors,
  CountdownConfig,
} from "./types";

export const MS_DAY = 86_400_000;

export const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Fallback de cada slot quando o racer não sobrescreve a cor: uma CSS var da paleta
 * do site (com hex de segurança). Assim o cronômetro herda a paleta por padrão e o
 * estado "vazio = segue a paleta" existe de verdade — não há mais hex fixos gravados.
 */
export const PALETTE_CSS_VARS: Required<CountdownColorSlots> = {
  bg: "var(--cards-background, #ffffff)",
  border: "var(--cards-border, #e2e8f0)",
  label: "var(--cards-body, #64748b)",
  numbers: "var(--cards-title, #0f172a)",
  units: "var(--cards-body, #64748b)",
  boxBg: "var(--general-primary, #3754e2)",
  boxText: "var(--buttons-text, #ffffff)",
};

/** Mapa do contrato antigo (enum) para número de dias — retrocompat de blocos salvos. */
const LEGACY_SHOW_WHEN: Record<string, number> = {
  always: 0,
  last30: 30,
  last15: 15,
  last7: 7,
  last3: 3,
  last1: 1,
};

/**
 * Janela de exibição em dias (0 = sempre). Lê o campo novo `showWithinDays` e cai
 * para o enum antigo `showWhen` quando o bloco foi salvo antes da migração.
 */
export const resolveShowWithinDays = (config?: CountdownConfig): number => {
  if (typeof config?.showWithinDays === "number") return config.showWithinDays;
  if (typeof config?.showWhen === "string") {
    return LEGACY_SHOW_WHEN[config.showWhen] ?? 0;
  }
  return 0;
};

export const isCountdownUrgent = (
  colors: CountdownColors | undefined,
  daysRemaining: number,
): boolean =>
  colors?.urgencyEnabled === true &&
  daysRemaining <= (colors?.urgencyThresholdDays ?? 3);

/**
 * Resolve a cor de um slot. Sem sobrescrita, devolve a CSS var da paleta. Na urgência,
 * usa o slot de urgência preenchido ou herda a cor-base resolvida.
 */
export const resolveCountdownColor = (
  colors: CountdownColors | undefined,
  isUrgent: boolean,
  key: keyof CountdownColorSlots,
): string => {
  const base = colors?.base?.[key] || PALETTE_CSS_VARS[key];
  if (!isUrgent) return base;
  return colors?.urgency?.[key] || base;
};

export interface CountdownParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export const computeCountdownParts = (diff: number): CountdownParts => ({
  days: Math.floor(diff / MS_DAY),
  hours: Math.floor((diff % MS_DAY) / 3_600_000),
  minutes: Math.floor((diff % 3_600_000) / 60_000),
  seconds: Math.floor((diff % 60_000) / 1000),
});

/**
 * Frase amigável do formato "Texto" do cronômetro, como no artefato de design.
 * Baseada na diferença de DIAS DE CALENDÁRIO entre agora e o fim (não no floor
 * de horas/24), para que "amanhã" case com a virada de dia real:
 * - mesmo dia   → "Termina hoje"
 * - próximo dia → "Termina amanhã"
 * - N dias      → "Termina em N dias"
 */
export const formatCountdownText = (now: number, target: number): string => {
  const startOfDay = (t: number) => {
    const d = new Date(t);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  const dayDiff = Math.round((startOfDay(target) - startOfDay(now)) / MS_DAY);

  if (dayDiff >= 2) return `Termina em ${dayDiff} dias`;
  if (dayDiff === 1) return "Termina amanhã";
  return "Termina hoje";
};
